import { useEffect } from "react";
import { Button, Modal, Form, Spinner } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";

import { useAuth } from "@/_zustand/auth";
import { useUpdateCredencial, useRefrescarSesion } from "@/hooks/useAuth";
import { validateRut, normalizarRut } from "@/lib";
import { getPostLoginPath } from "@/hooks/useCompanyFlow";
import Logo from "@/assets/imgs/logo.png";
import "./modalcredential.css";

// Mensaje unico para errores de negocio y de red: no exponer mensajes tecnicos
// del backend (p.ej. el error.message de PostgreSQL que devuelve update.post.ts:33).
const MSG_ERROR_GUARDAR = "No pudimos guardar tus datos. Intentalo nuevamente.";

export const ModalCredential = ({ show, setShow, user }) => {
  const navigate = useNavigate();
  const { setSesionAuth } = useAuth();
  const updateCredencial = useUpdateCredencial();
  const refrescarSesion = useRefrescarSesion();

  const defaultValues = {
    usuario_rut: "",
    usuario_nombre: "",
    usuario_apellido: "",
    usuario_id: "",
  };

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const getFormErrorMessage = (name) =>
    errors[name] && (
      <small className="text-danger">{errors[name].message}</small>
    );

  // Resuelve CA 22 (limpiar residuos entre intentos) y CA 23 (prellenar nombre/apellido del proveedor).
  // Login.jsx hace setUser(resp.user) ANTES de setShow(true), por lo que user ya tiene los datos
  // correctos cuando show pasa a true. usuario_rut siempre vacio: nunca viene del proveedor social.
  useEffect(() => {
    if (show) {
      reset({
        usuario_rut: "",
        usuario_nombre: user?.usuario_nombre ?? "",
        usuario_apellido: user?.usuario_apellido ?? "",
        usuario_id: "",
      });
    }
  }, [show, user, reset]);

  const enviando = updateCredencial.isLoading || refrescarSesion.isLoading;

  const handleClose = () => {
    // No permitir cerrar mientras la mutacion (o el refresco de sesion de
    // CA-19) esta en vuelo (evita navegacion sorpresa si la respuesta llega
    // despues del cierre y llama setSesionAuth + navigate).
    if (enviando) return;
    setShow(false);
  };

  const onSubmit = (formData) => {
    // Defensa extra contra doble Enter antes de que isLoading se propague.
    if (enviando) return;

    const rutNormalizado = normalizarRut(formData.usuario_rut);
    const payload = {
      ...formData,
      usuario_rut: rutNormalizado,
      usuario_id: user.usuario_id,
    };

    updateCredencial.mutateAsync(payload)
      .then((resp) => {
        if (resp.status === "error") {
          // No mostrar resp.message (puede ser un error tecnico de PostgreSQL).
          // Se conserva para diagnostico sin llegar al usuario.
          console.error("[ModalCredential] update credencial:", resp?.message);
          toast.error(MSG_ERROR_GUARDAR);
          return;
        }

        // Guard: si el backend devolvio status "success" pero data vacio o ausente,
        // no autenticar — evita user:{} o user:undefined en el store persistido,
        // que revienta aguas abajo en MenuNuevo/CartPage.
        if (!resp.data || Object.keys(resp.data).length === 0) {
          console.error("[ModalCredential] update credencial: data vacio en respuesta exitosa", resp);
          toast.error(MSG_ERROR_GUARDAR);
          return;
        }

        // CA-19: el token de sesion social se emitio SIN la claim `rut` (el
        // usuario no la tenia al firmarlo). Antes de marcar la sesion como
        // completa hay que renovarla via refreshToken, que re-lee el rut
        // recien guardado en BD — si no, el header `rut` (derivado de la
        // claim, ver ApiCore._headers/rutDeSesion) seguiria sin el, mientras
        // storeByDistance ya recibiria un usuario "con rut" por otro lado
        // (CA-6: prohibido claim vieja + header nuevo o viceversa).
        refrescarSesion.mutateAsync()
          .then((huboSesion) => {
            if (!huboSesion) {
              toast.error(MSG_ERROR_GUARDAR);
              return;
            }

            toast.success("Sesion iniciada correctamente");
            setSesionAuth(resp.data);
            setShow(false);
            navigate(getPostLoginPath());
          })
          .catch(() => {
            toast.error(MSG_ERROR_GUARDAR);
          });
      })
      .catch(() => {
        // El reporte a Sentry ya ocurre en ApiCore.js:495 — no duplicarlo aqui.
        toast.error(MSG_ERROR_GUARDAR);
      });
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      contentClassName="credential-modal-content"
      backdrop={enviando ? "static" : true}
      keyboard={!enviando}
    >
      <Modal.Header closeButton className="credential-modal-header">
        <img src={Logo} alt="Openmatic" className="credential-modal-logo" />
        <Modal.Title className="credential-modal-title">Completar informacion</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
        <Modal.Body>
          <p className="credential-modal-subtitle">
            Nos falta un poco mas de informacion para terminar de crear tu cuenta.
          </p>
          <p className="credential-modal-note">
            Necesitamos estos datos para que puedas comprar. Si cierras, tendras que volver a iniciar sesion.
          </p>

          <div className="credential-modal-group">
            <Controller name="usuario_nombre" control={control}
              rules={{ required: "Debe indicar el nombre" }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2">
                  <Form.Label className="mb-2">Nombre</Form.Label>
                  <Form.Control type="text" placeholder="Nombre" id={field.name} {...field} isInvalid={fieldState.invalid} />
                  {getFormErrorMessage("usuario_nombre")}
                </Form.Group>
              )}
            />
          </div>
          <div className="credential-modal-group">
            <Controller name="usuario_apellido" control={control}
              rules={{ required: "Debe indicar el apellido" }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2">
                  <Form.Label className="mb-2">Apellido</Form.Label>
                  <Form.Control type="text" placeholder="Apellido" id={field.name} {...field} isInvalid={fieldState.invalid} />
                  {getFormErrorMessage("usuario_apellido")}
                </Form.Group>
              )}
            />
          </div>
          <div className="credential-modal-group">
            <Controller name="usuario_rut" control={control}
              rules={{
                required: "Debe indicar el rut",
                validate: (value) => {
                  if (!value) return "Debe indicar el rut";
                  const normalizado = normalizarRut(value);
                  // Formato obligatorio: digitos + guion ASCII + digito/K.
                  // Identico al de RegisterPage.jsx:174, validado sobre el valor normalizado
                  // (que ya convirtio puntos, guiones Unicode, NBSP, etc.).
                  if (!/^[0-9]+-[0-9kK]{1}$/.test(normalizado)) {
                    return "Rut no valido (formato: 12345678-9)";
                  }
                  if (normalizado.length < 9 || normalizado.length > 12) {
                    return "Rut no valido";
                  }
                  // Validar digito verificador con algoritmo modulo 11
                  if (!validateRut(normalizado)) {
                    return "Rut no valido (digito verificador incorrecto)";
                  }
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <Form.Group className="position-relative mb-2">
                  <Form.Label className="mb-2">Rut (Sin puntos Ej:12345678-9)</Form.Label>
                  <Form.Control type="text" placeholder="12345678-9" id={field.name} {...field} isInvalid={fieldState.invalid} />
                  {getFormErrorMessage("usuario_rut")}
                </Form.Group>
              )}
            />
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button type="submit" className="credential-modal-submit" disabled={enviando}>
            {enviando ? (
              <>
                <Spinner animation="border" size="sm" role="status" aria-hidden="true"></Spinner>
                Enviando...
              </>
            ) : (
              "Enviar"
            )}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
